"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFeatureFlag = void 0;
const client_side_services_1 = require("@ampcore/client-side-services");
const react_1 = require("react");
/**
 * Hook to access a LaunchDarkly feature flag value with real-time updates.
 *
 * `isReady` turns true once the flag read has settled — whether LaunchDarkly
 * initialized in time or the wait timed out. On timeout `flagValue` is the
 * supplied default, which is the intended value when the service cannot be
 * reached; consumers act on `flagValue` alone and need not distinguish the two.
 */
function useFeatureFlag(flagKey, defaultValue) {
    const ldService = client_side_services_1.LaunchDarklyService.getInstance();
    const [flagValue, setFlagValue] = (0, react_1.useState)(defaultValue);
    const [isReady, setIsReady] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        const override = client_side_services_1.FeatureFlagOverrides.get(flagKey);
        if (override !== undefined) {
            setFlagValue(override);
            setIsReady(true);
            return;
        }
        const ldClient = ldService.getClient();
        let isMounted = true;
        let updateFlag;
        ldService.waitForInitialization().then(() => {
            if (!isMounted)
                return;
            if (!ldClient) {
                setIsReady(true);
                return;
            }
            updateFlag = () => {
                const value = ldClient.variation(flagKey, defaultValue);
                setFlagValue(value);
            };
            updateFlag();
            setIsReady(true);
            ldClient.on(`change:${flagKey}`, updateFlag);
        });
        return () => {
            isMounted = false;
            if (ldClient && typeof ldClient.off === 'function' && updateFlag) {
                ldClient.off(`change:${flagKey}`, updateFlag);
            }
        };
    }, [ldService, flagKey, defaultValue]);
    return { flagValue, isReady };
}
exports.useFeatureFlag = useFeatureFlag;
